import { useEffect, useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';

type PostLayoutSize =
  | 'mobile'
  | 'narrow'
  | 'small'
  | 'regular'
  | 'wide'
  | 'xWide';

interface Breakpoints {
  narrow: number;
  small: number;
  regular: number;
  wide: number;
  xWide: number;
}

function getPostLayoutSize(
  currentSize: number,
  { narrow, small, regular, wide, xWide }: Breakpoints,
): PostLayoutSize {
  if (currentSize < narrow) return 'mobile';
  if (currentSize < small) return 'narrow';
  if (currentSize < regular) return 'small';
  if (currentSize < wide) return 'regular';
  if (currentSize < xWide) return 'wide';
  return 'xWide';
}

// This hook is replicating the layout sizes from post.scss
// If you change the breakpoints here, also change them in post.scss
function usePostLayoutSize() {
  const [currentSize, setCurrentSize] = useState(() => window.innerWidth);
  const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
  const isRightPanelOpen = useAppSelector((state) => state.ui.rightPanel.open);

  useEffect(() => {
    const handleResize = () => {
      setCurrentSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isRightPanelOpen && isSidebarOpen) {
    return getPostLayoutSize(currentSize, {
      narrow: 768,
      small: 1280,
      regular: 1440,
      wide: 1600,
      xWide: 1920,
    });
  }

  if (isSidebarOpen || isRightPanelOpen) {
    return getPostLayoutSize(currentSize, {
      narrow: 768,
      small: 992,
      regular: 1280,
      wide: 1440,
      xWide: 1600,
    });
  }

  return getPostLayoutSize(currentSize, {
    narrow: 768,
    small: 768,
    regular: 1024,
    wide: 1280,
    xWide: 1440,
  });
}

export default usePostLayoutSize;
